import { useState } from 'react';
import type { Pago } from '../../../api/ops/pagos';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { rolesDe } from '../../../logica/acceso';
import { dinero } from '../../../logica/dinero';
import { diaYHora } from '../../../logica/fechasCortas';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { useNegocioPagos } from '../negocio';
import { AYUDA, CONCEPTO, FORMA_PAGO, PERMISOS } from '../textos';
import { avisoRegistrado } from './avisos';
import { Registrar, type Cobrable } from './Registrar';
import '../../../componentes/apartado.css';
import '../pagos.css';

/**
 * «Cobrar» (entrada aparte en Operativa, G276; pantalla 4.7 para Recepción y quien registra pagos en el local):
 * «Registrar un pago» arriba y, debajo, «Registrados por ti en esta sesión» (G289: solo lo de esta sesión, que el panel ya
 * conoce por las respuestas; se pierde al cerrar). Sin lista de «Pendiente de cobrar hoy» (G288). En solo lectura no se
 * registra (la API lo bloquea).
 */
export function Cobrar(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const puede = permisos.puede(PERMISOS.registrar);
    const n = useNegocioPagos(ops, negocio);
    const [abierto, setAbierto] = useState(false);
    const [registrados, setRegistrados] = useState<{ pago: Pago; cobrable: Cobrable }[]>([]);

    const acciones: AccionCabecera[] = puede ? [{ clave: 'registrar', texto: 'Registrar un pago', icono: 'banknote', desactivado: soloLectura, onClick: () => { setAbierto(true); } }] : [];
    useCabecera({ ayuda: puede ? AYUDA.caja : [], acciones });

    if (!puede) {
        return <EstadoSinPermiso seccion="Cobrar" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }

    return (
        <div className="rv-ca-apartado">
            <RegistradosEnLaSesion registrados={registrados} soloLectura={soloLectura} />
            <Registrar
                ops={ops}
                negocio={negocio}
                movil={movil}
                abierto={abierto}
                n={n}
                onCerrar={() => { setAbierto(false); }}
                onRegistrado={(pago, cobrable) => {
                    setAbierto(false);
                    setRegistrados((r) => [{ pago, cobrable }, ...r]);
                    avisar({ tipo: 'ok', texto: avisoRegistrado(pago, cobrable) });
                }}
                onError={(texto) => { avisar({ tipo: 'error', texto }); }}
            />
        </div>
    );
}

export function RegistradosEnLaSesion({ registrados, soloLectura }: { registrados: readonly { pago: Pago; cobrable: Cobrable }[]; soloLectura: boolean }) {
    if (registrados.length === 0) {
        return (
            <div className="rv-ca-vacia">
                <span className="rv-ca-vacia__baldosa"><Icono nombre="banknote" /></span>
                <strong>¿Te han pagado en el local?</strong>
                <span>{soloLectura ? 'El negocio está en solo lectura: ahora no se pueden registrar pagos.' : 'Con «Registrar un pago» anotas lo que te pagan en efectivo o con el datáfono del negocio: busca la reserva o la cita de hoy y escribe el importe.'}</span>
            </div>
        );
    }
    const ahora = new Date();
    return (
        <section className="rv-ca-bloque">
            <p className="rv-ca-ceja">Registrados por ti en esta sesión</p>
            <div className="rv-ca-lista" role="list" aria-label="Registrados por ti en esta sesión">
                {registrados.map(({ pago, cobrable }) => (
                    <div key={pago.id} role="listitem" className="rv-ca-fila">
                        <span className="rv-ca-baldosa"><Icono nombre={pago.method === 'card_terminal' ? 'credit-card' : 'banknote'} /></span>
                        <span className="rv-ca-fila__texto">
                            <strong>{cobrable.titulo}</strong>
                            <span>{[CONCEPTO[pago.concept], pago.method == null ? '' : FORMA_PAGO[pago.method][0], diaYHora(pago.recorded_at ?? pago.created_at, ahora)].filter((x) => x !== '').join(' · ')}</span>
                        </span>
                        <strong className="rv-pg-importe">{dinero(pago.amount)}</strong>
                    </div>
                ))}
            </div>
        </section>
    );
}
